import { useMutation, UseMutationResult, useQueryClient } from 'react-query';

import { getTokenWithType } from './useAuth';

import { ftpSettings as saveFtpSettings } from 'utils/urls';

import { FTPSettings } from 'types/ftpSettings';
import { ErrorModal } from 'types/modalTypes';

export async function postFtpSettings({
  ftpSettings,
  accountId,
  privateKey,
  mode,
}: TVariables): Promise<TResult> {
  const formData = new FormData();
  formData.append('ftpSettings', JSON.stringify(ftpSettings));
  if (privateKey) formData.append('privateKey', privateKey);
  const response = await fetch(saveFtpSettings(accountId), {
    method: mode === 'edit' ? 'PUT' : 'POST',
    headers: {
      Authorization: getTokenWithType(),
    },
    body: formData,
  });
  return response.json();
}

type TError = ErrorModal;

type TResult = {
  Data: FTPSettings;
  IsSuccess: boolean;
  StatusCode: number;
} & ErrorModal &
  Array<string>;

type TVariables = {
  ftpSettings: FTPSettings;
  accountId: number;
  privateKey?: File;
  mode?: string;
};

type TSnapshot = unknown;

function useSaveFtpSettings(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  TSnapshot
> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ ftpSettings, accountId, privateKey, mode }: TVariables) => {
      const newFtp = await postFtpSettings({
        ftpSettings,
        accountId,
        privateKey,
        mode,
      });
      if (newFtp.StatusCode === 200) {
        queryClient.invalidateQueries(['get_ftp_settings']);
        return newFtp;
      } else if (newFtp.Errors) throw newFtp;
      else if (newFtp.length) throw newFtp[0];
      return newFtp;
    },
  );
}

export { useSaveFtpSettings };
